import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, ShieldCheck, Users, X } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useAuth } from "@/context/AuthContext";

const STATUS_LABEL = {
  pending: "Pending review",
  rejected: "Rejected",
};

/**
 * Staff-only moderation queue.
 * Lists every server currently in a hidden moderation state
 * (`pending`, `changes_requested`, `rejected`) with quick decision buttons.
 */
export default function AdminModeration() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [noteFor, setNoteFor] = useState(null);
  const [noteAction, setNoteAction] = useState("");
  const [noteText, setNoteText] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user || (user.role !== "admin" && user.role !== "owner")) navigate("/");
  }, [user, loading, navigate]);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/moderation/queue");
      setItems(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load queue");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (user?.role === "admin" || user?.role === "owner") load();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (s) => {
    if (!confirm(`Approve "${s.name}"? This will publish the listing and notify #new-servers.`)) return;
    try {
      await api.post(`/admin/moderation/${s.id}/approve`);
      toast.success("Approved & published");
      setItems((prev) => prev.filter((x) => x.id !== s.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    }
  };

  const openNoteFor = (s) => {
    setNoteFor(s);
    setNoteAction("reject");
    setNoteText("");
  };

  const submitNote = async () => {
    if (!noteFor) return;
    try {
      await api.post(`/admin/moderation/${noteFor.id}/reject`, {
        note: noteText.trim(),
      });
      toast.success("Rejected");
      setItems((prev) => prev.filter((x) => x.id !== noteFor.id));
      setNoteFor(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    }
  };

  if (loading || !user) return null;
  if (user.role !== "admin" && user.role !== "owner") return null;

  return (
    <div className="pt-24 pb-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="admin-moderation-page">
      <SEO title="Moderation queue" description="Serverlistr moderation queue." path="/admin/moderation" noindex />
      <Breadcrumbs items={[{ name: "Staff console", href: "/admin" }, { name: "Moderation" }]} />

      <button onClick={() => navigate("/admin")} className="mt-2 inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white">
        <ArrowLeft className="w-4 h-4" /> Back to staff console
      </button>

      <div className="mt-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-yellow-300">
        <ShieldCheck className="w-3.5 h-3.5" /> Staff-only · gate for new listings
      </div>
      <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-black">Moderation queue</h1>
      <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
        Every new submission lands here first. Approve to publish, request changes to bounce it back to the owner, or reject spam/scam/NSFW outright.
      </p>

      {busy && items.length === 0 ? (
        <div className="mt-10 text-sm text-zinc-500">Loading queue…</div>
      ) : items.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.03] p-8 text-center">
          <div className="font-heading text-lg font-black text-emerald-300">All caught up 🎉</div>
          <p className="mt-1 text-sm text-zinc-400">No servers waiting for review right now.</p>
        </div>
      ) : (
        <ul className="mt-8 space-y-4">
          {items.map((s) => (
            <li
              key={s.id}
              className="rounded-2xl border border-white/10 bg-[#0A0A0F] p-5"
              data-testid={`queue-item-${s.id}`}
            >
              <div className="flex items-start gap-4">
                {s.icon_url ? (
                  <img src={s.icon_url} alt="" className="w-14 h-14 rounded-xl border border-white/10 shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-blurple/20 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link
                      to={`/servers/${s.slug || s.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-heading text-lg font-black hover:text-neon-cyan"
                    >
                      {s.name}
                    </Link>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${
                        s.moderation_status === "pending"
                          ? "bg-yellow-500/10 border-yellow-500/40 text-yellow-300"
                          : s.moderation_status === "changes_requested"
                          ? "bg-blue-500/10 border-blue-500/40 text-blue-300"
                          : "bg-red-500/10 border-red-500/40 text-red-300"
                      }`}
                    >
                      {STATUS_LABEL[s.moderation_status] || s.moderation_status}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-zinc-300 line-clamp-2">{s.description}</p>
                  {s.owner && (
                    <div
                      data-testid={`queue-owner-${s.id}`}
                      className="mt-3 flex items-center gap-2 text-xs"
                    >
                      {s.owner.picture ? (
                        <img
                          src={s.owner.picture}
                          alt=""
                          className="w-6 h-6 rounded-full border border-white/10"
                        />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-blurple/30" />
                      )}
                      <span className="text-zinc-500">Submitted by</span>
                      <span className="text-zinc-100 font-semibold">
                        {s.owner.name || "Unknown"}
                      </span>
                      {s.owner.email && (
                        <span className="text-zinc-600 truncate">· {s.owner.email}</span>
                      )}
                      {s.owner.discord_id && (
                        <span className="text-zinc-600 font-mono">· Discord ID {s.owner.discord_id}</span>
                      )}
                    </div>
                  )}
                  {s.long_description && (
                    <details className="mt-2 text-xs text-zinc-500 hover:text-zinc-300">
                      <summary className="cursor-pointer select-none">Full description</summary>
                      <p className="mt-2 whitespace-pre-wrap text-zinc-300">{s.long_description}</p>
                    </details>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-zinc-500 tabular-nums">
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {(s.member_count || 0).toLocaleString()}</span>
                    <span>Categories: {(s.categories || []).join(", ") || "—"}</span>
                    <span>Tags: {(s.tags || []).map((t) => `#${t}`).join(", ") || "—"}</span>
                    <a href={s.invite_url} target="_blank" rel="noreferrer" className="text-neon-cyan hover:underline">
                      Open invite ↗
                    </a>
                  </div>
                  {s.moderation_note && (
                    <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.02] p-2 text-xs text-zinc-400">
                      <span className="font-semibold text-zinc-300">Last note: </span>{s.moderation_note}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2 justify-end">
                <button
                  onClick={() => openNoteFor(s)}
                  data-testid={`reject-${s.id}`}
                  className="h-9 px-3 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-semibold inline-flex items-center gap-1.5"
                >
                  <X className="w-3.5 h-3.5" /> Reject
                </button>
                <button
                  onClick={() => approve(s)}
                  data-testid={`approve-${s.id}`}
                  className="h-9 px-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-black inline-flex items-center gap-1.5"
                >
                  <Check className="w-3.5 h-3.5" /> Approve
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {noteFor && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setNoteFor(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-[#0A0A0F] border border-white/10 p-6">
            <h3 className="font-heading text-lg font-black">Reject — {noteFor.name}</h3>
            <p className="mt-1 text-xs text-zinc-500">
              Optional — tell the owner why. They&apos;ll see this on their dashboard.
            </p>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value.slice(0, 1000))}
              rows={5}
              placeholder="e.g. Violates rule 3 — invite-farm server."
              className="mt-3 w-full px-3 py-2 bg-[#12121A] border border-white/10 rounded-lg text-sm placeholder-zinc-600 focus:border-blurple focus:outline-none resize-y"
              data-testid="mod-note-input"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setNoteFor(null)} className="h-10 px-4 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-semibold">Cancel</button>
              <button
                onClick={submitNote}
                data-testid="mod-note-confirm"
                className="h-10 px-5 rounded-lg font-bold bg-red-500 hover:bg-red-400 text-white"
              >
                Confirm reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
